///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story playback dataset mounting. A stop's datasetIds reference cloud
/// dataset refs in the story pool; on arrival the referenced files are
/// fetched from the CDN and run through the normal import pipeline (same
/// as dataset share links), so the data renders on the globe for anyone —
/// including share recipients without an account. Fail-soft: a deleted or
/// unreachable dataset logs a warning and the stop plays without it.
///

import { CSVFileHandler, GeoJSONFileHandler } from "@/components/features/file-drop/handlers"
import { mountImageOverlay } from "@/utils/imageOverlayMount"
import { datasetUrl } from "@/utils/datasetUrl"
import { updateDatasetWindow } from "@/redux/dialogSlice"
import { appStore } from "@/redux/store"
import type { Story, StoryDatasetRef, StoryStop } from "@/redux/storiesSlice"

/** Refs currently downloading (dedup across rapid stop changes) */
const inFlight = new Set<string>()

/** True when a dataset window already carries this cloud identity */
function isMounted(datasetId: string): boolean {
	const windows = appStore.getState().dialog.datasetWindows
	return Object.values(windows).some(w => w.cloudDataset?.id === datasetId)
}

async function mountDataset(ref: StoryDatasetRef): Promise<void> {
	const response = await fetch(datasetUrl(ref.cdnUrl))
	if (!response.ok) {
		throw new Error(`Download failed (${response.status})`)
	}
	const blob = await response.blob()
	const file = new File([blob], ref.filename, {
		type: ref.format === "geojson"
			? "application/geo+json"
			: ref.format === "image" ? (blob.type || "image/png") : "text/csv",
	})

	// Image ground overlays drape at the placement captured with the story;
	// the properties window opens unfocused so the globe stays the surface
	if (ref.format === "image") {
		const windowId = await mountImageOverlay(appStore.dispatch, () => appStore.getState(), file, {
			bounds: ref.bounds,
			opacity: ref.opacity,
			bringToFront: false,
			cloudDataset: {
				id: ref.id,
				name: ref.name,
				filename: ref.filename,
				format: ref.format,
				cdnUrl: ref.cdnUrl,
			},
		})
		if (!windowId) {
			throw new Error("Image overlay could not be mounted")
		}
		return
	}

	const context = { dispatch: appStore.dispatch, getState: () => appStore.getState() }
	const handler = ref.format === "geojson"
		? new GeoJSONFileHandler(context)
		: new CSVFileHandler(context)
	handler.initialize()
	try {
		// The drawer stays closed: the globe is the story surface, the
		// data table shouldn't steal the screen mid-playback
		const windowId = await handler.handleFile(file, { openDrawer: false })
		if (windowId) {
			appStore.dispatch(updateDatasetWindow({
				id: windowId,
				updates: { cloudDataset: { ...ref } },
			}))
		}
	} finally {
		handler.cleanup()
	}
}

/**
 * Mount the cloud datasets a stop references (fire-and-forget from the
 * story player). Already-mounted and in-flight refs are skipped; failures
 * are logged and never interrupt playback.
 */
export function mountStoryDatasets(story: Story, stop: StoryStop): void {
	if (!stop.datasetIds || stop.datasetIds.length === 0) return

	for (const datasetId of stop.datasetIds) {
		const ref = story.datasets.find(d => d.id === datasetId)
		if (!ref || isMounted(ref.id) || inFlight.has(ref.id)) continue

		inFlight.add(ref.id)
		void mountDataset(ref)
			.catch(error => {
				console.warn(`[stories] Could not mount dataset "${ref.name}":`, error)
			})
			.finally(() => inFlight.delete(ref.id))
	}
}
